/**
 * Custom Hook: useTemplateManagement
 *
 * Manages template-related state and operations for chat functionality.
 * Extracted from Chat.tsx to reduce component complexity and improve maintainability.
 */

import { Dispatch, SetStateAction, useCallback } from "react";
import { useDispatch } from "react-redux";
import { getTemplateDetailsForQuestionEdit as getTemplateDetailsForQuestionEditHelper, TemplateDetailsForQuestionEdit } from "../utils/templateHelpers";

interface UseTemplateManagementProps {
    setHideDynamicTemplatePopup: Dispatch<SetStateAction<boolean>>;
    setSelectedTemplate: (templateId: number) => void;
    setSelectedTemplateContent: (content: string) => void;
    setSelectedTemplateContentId: (id: string) => void;
    setTemplateKeywords: (keywords: string) => void;
    setIsHistoryPageInitialLoading: (loading: boolean) => void;
    setIsNewChatThreadInitialLoading?: (loading: boolean) => void;
    questionInputRefs: any;
    currentView: string;
    QuestionInputReducerAction: any;
    templateContents: any[];
}

interface UseTemplateManagementReturn {
    handleTemplateIconClick: () => void;
    handleTemplateClick: (templateId: number) => void;
    handleTemplateContents: (contents: any, tempId: string | number | null | undefined) => void;
    handleTemplateContentsBySuggestions: (contents: any, tempId: string | number | null | undefined) => void;
    handleClearTemplateContent: () => void;
    getTemplateDetailsForQuestionEdit: (templateIdValue: string) => {
        templateContent: TemplateDetailsForQuestionEdit["templateContent"];
        templateKeyWords: string | undefined;
    };
}

/**
 * Hook for managing template operations in chat
 */
export const useTemplateManagement = ({
    setHideDynamicTemplatePopup,
    setSelectedTemplate,
    setSelectedTemplateContent,
    setSelectedTemplateContentId,
    setTemplateKeywords,
    setIsHistoryPageInitialLoading,
    setIsNewChatThreadInitialLoading,
    questionInputRefs,
    currentView,
    QuestionInputReducerAction,
    templateContents
}: UseTemplateManagementProps): UseTemplateManagementReturn => {
    const dispatch = useDispatch();

    /**
     * Toggles the dynamic template popup visibility
     */
    const handleTemplateIconClick = useCallback(() => {
        setHideDynamicTemplatePopup(previous => !previous);
    }, [setHideDynamicTemplatePopup]);

    /**
     * Handles template selection
     */
    const handleTemplateClick = useCallback(
        (templateId: number) => {
            setSelectedTemplate(templateId);
        },
        [setSelectedTemplate]
    );

    /**
     * Applies template contents and updates related state
     */
    const handleTemplateContentsBySuggestions = useCallback(
        (contents: any, tempId: string | number | null | undefined) => {
            const questionInputValue = questionInputRefs?.current[currentView]?.getCurrentQuestionInputValue();
            const normalizedTemplateId = tempId === null || tempId === undefined ? "" : String(tempId);
            const hasAppliedTemplateId = normalizedTemplateId !== "" && normalizedTemplateId !== "None";
            const hasQuestionText = !!(questionInputValue && String(questionInputValue).trim() !== "");
            const isTemplateApplied = hasQuestionText || hasAppliedTemplateId;

            dispatch(QuestionInputReducerAction.updateTemplateAppliedFlag(isTemplateApplied));

            const textContent = contents;
            setSelectedTemplateContent(textContent);
            setSelectedTemplateContentId(normalizedTemplateId);

            const keyvalues = Object.values(contents?.fields ?? {}).join(" ");
            setTemplateKeywords(keyvalues);
            setIsHistoryPageInitialLoading(false);
            setIsNewChatThreadInitialLoading?.(false);
        },
        [
            questionInputRefs,
            currentView,
            dispatch,
            QuestionInputReducerAction,
            setSelectedTemplateContent,
            setSelectedTemplateContentId,
            setTemplateKeywords,
            setIsHistoryPageInitialLoading,
            setIsNewChatThreadInitialLoading
        ]
    );

    /**
     * Handles template content selection and closes popup
     */
    const handleTemplateContents = useCallback(
        (contents: any, tempId: string | number | null | undefined) => {
            handleTemplateContentsBySuggestions(contents, tempId);
            handleTemplateIconClick();
        },
        [handleTemplateContentsBySuggestions, handleTemplateIconClick]
    );

    /**
     * Clears template content and resets related state
     */
    const handleClearTemplateContent = useCallback(() => {
        dispatch(QuestionInputReducerAction.updateTemplateAppliedFlag(false));
        setSelectedTemplateContent("");
        setSelectedTemplateContentId("");
    }, [dispatch, QuestionInputReducerAction, setSelectedTemplateContent, setSelectedTemplateContentId]);

    /**
     * Gets template details for question edit operations
     */
    const getTemplateDetailsForQuestionEdit = useCallback(
        (templateIdValue: string): TemplateDetailsForQuestionEdit => getTemplateDetailsForQuestionEditHelper(templateIdValue, templateContents),
        [templateContents]
    );

    return {
        handleTemplateIconClick,
        handleTemplateClick,
        handleTemplateContents,
        handleTemplateContentsBySuggestions,
        handleClearTemplateContent,
        getTemplateDetailsForQuestionEdit
    };
};
